import ClassModel from "#src/models/ClassModel.ts";
import GroupRegistrationModel, {
  isGroupRegistration,
} from "#src/models/GroupRegistrationModel.ts";
import MembershipModel from "#src/models/MembershipModel.ts";
import PrivateSerieModel, {
  isPrivateSerie,
} from "#src/models/PrivateSerieModel.ts";
import UserModel from "#src/models/UserModel.ts";
import calculateProratedRegistrationPrice from "#src/utils/calculateProratedRegistrationPrice.ts";
import { moment } from "#src/utils/dateTime.ts";
import {
  AmountType,
  discounts,
  DiscountType,
  GroupRegistrationStatus,
} from "#types/code-generator.ts";
import { intersection } from "es-toolkit/compat";

interface CalculateDiscountsInput {
  discounts: discounts["discounts"];
  registrations: Array<
    GroupRegistrationModel | PrivateSerieModel | MembershipModel
  >;
  startDate: Date;
  students: UserModel[];
  organization: {
    departments: {
      id: string;
    }[];
    timezone: string;
  };
}

export default ({
  discounts,
  students,
  registrations,
  startDate,
  organization,
}: CalculateDiscountsInput) => {
  const pricesByRegistration = new Map<GroupRegistrationModel, number>();
  const getRegistrationPrice = (registration: GroupRegistrationModel) => {
    if (!pricesByRegistration.has(registration)) {
      pricesByRegistration.set(
        registration,
        calculateProratedRegistrationPrice({
          classItem: new ClassModel(registration.class),
          endDate: moment(startDate).endOf("month").toDate(),
          startDate,
          organization,
        })
      );
    }
    return pricesByRegistration.get(registration);
  };
  const results: Array<{
    discount: discounts["discounts"][0];
    ids: string[];
  }> = [];
  const eligibleDiscounts = discounts.filter((d) => {
    if (d.availableForAllOfferings) {
      if (organization.departments.length > 1) {
        return registrations.some((r) => {
          if (isGroupRegistration(r)) {
            return r.class?.department?.id === d.department?.id;
          }

          if (isPrivateSerie(r)) {
            return r.offering?.department?.id === d.department?.id;
          }
        });
      }
      return true;
    }

    for (const r of registrations) {
      if (
        r &&
        isGroupRegistration(r) &&
        r.class?.discounts?.find((cd) => cd.id === d.id)
      ) {
        return true;
      }

      if (
        r &&
        isPrivateSerie(r) &&
        r.offering?.discounts?.find((od) => od.id === d.id)
      ) {
        return true;
      }
    }

    return false;
  });

  // console.log("eligibleDiscounts", eligibleDiscounts);

  eligibleDiscounts.forEach((d) => {
    const minForDiscount = d.min;
    const maxForDiscount = d.max;

    if (d.discountType === DiscountType.MultiClass) {
      students.forEach((s) => {
        const registrationsForStudent = registrations.filter((r) => {
          if (isGroupRegistration(r)) {
            return (
              ((r.student && r.student.id === s.id) ||
                (r.roster && r.roster.find((rs) => rs.id === s.id))) &&
              r.status !== GroupRegistrationStatus.Waitlist
            );
          }
          return false;
        });

        const eligibleMultiClassRegistrations = registrationsForStudent
          .filter((r) => {
            if (isGroupRegistration(r)) {
              const price = getRegistrationPrice(r);
              let departmentEligible = true;

              if (organization.departments.length > 1) {
                departmentEligible =
                  r.class?.department?.id === d.department?.id;
              }

              return (
                departmentEligible &&
                (price > 0 || r.class.chargeAmounts.length > 0)
              );
            }
            return false;
          })
          .map((r) => r.id);

        if (
          eligibleMultiClassRegistrations.length >= minForDiscount &&
          eligibleMultiClassRegistrations.length <= maxForDiscount
        ) {
          const existingMultiClassDiscountIndex = results.findIndex(
            (rd) =>
              rd.discount.discountType === DiscountType.MultiClass &&
              rd.discount.department?.id === d.department?.id
          );

          if (existingMultiClassDiscountIndex >= 0) {
            const existingMultiClassDiscount =
              results[existingMultiClassDiscountIndex];
            if (existingMultiClassDiscount.discount.min > d.min) {
              results.splice(existingMultiClassDiscountIndex, 1);
            }
          }

          results.push({
            discount: d,
            ids: eligibleMultiClassRegistrations,
          });
        }
      });
    }

    if (d.discountType === DiscountType.MultiStudent) {
      const eligibleMultiStudentRegistrations = registrations.filter((r) => {
        if (d.availableForAllOfferings) {
          if (isGroupRegistration(r) && organization.departments.length > 1) {
            return r.class?.department?.id === d.department?.id;
          }

          return true;
        }

        if (isGroupRegistration(r)) {
          const price = getRegistrationPrice(r);
          let departmentEligible = true;

          if (organization.departments.length > 1) {
            departmentEligible = r.class?.department?.id === d.department?.id;
          }

          // console.log("price", price);

          return (
            departmentEligible &&
            (price > 0 || r.class.chargeAmounts.length > 0) &&
            r.status !== GroupRegistrationStatus.Waitlist &&
            r.class?.discounts.find((cd) => cd.id === d.id)
          );
        }

        return false;
      });

      // console.log(
      //   "eligibleMultiStudentRegistrations",
      //   eligibleMultiStudentRegistrations
      // );

      const studentsInEligibleMultiStudentRegistrations =
        eligibleMultiStudentRegistrations.reduce((acc, r) => {
          if (isGroupRegistration(r)) {
            if (r.student && !acc.includes(r.student.id)) {
              acc.push(r.student.id);
            }
            if (
              r.roster &&
              intersection(
                r.roster.map((rs) => rs.id),
                acc
              ).length === 0
            ) {
              r.roster.forEach((rs) => acc.push(rs.id));
            }
          }
          return acc;
        }, []);

      // console.log(
      //   "eligibleMultiStudentRegistrations",
      //   eligibleMultiStudentRegistrations.length
      // );
      // console.log("students", studentsInEligibleMultiStudentRegistrations);

      if (
        studentsInEligibleMultiStudentRegistrations.length >= minForDiscount &&
        studentsInEligibleMultiStudentRegistrations.length <= maxForDiscount
      ) {
        const existingMultiStudentDiscountIndex = results.findIndex(
          (rd) =>
            rd.discount.discountType === DiscountType.MultiStudent &&
            rd.discount.department?.id === d.department?.id
        );
        const existingMultiStudentDiscount =
          results[existingMultiStudentDiscountIndex];

        if (existingMultiStudentDiscountIndex >= 0) {
          if (
            existingMultiStudentDiscount.discount.min > d.min ||
            existingMultiStudentDiscount.discount.id === d.id
          ) {
            results.splice(existingMultiStudentDiscountIndex, 1);
          }
        }

        results.push({
          discount: {
            ...d,
            amount:
              d.amountType === AmountType.Dollar
                ? Math.ceil(
                    d.amount *
                      studentsInEligibleMultiStudentRegistrations.length
                  )
                : d.amount,
          },
          ids: studentsInEligibleMultiStudentRegistrations,
        });
      }
    }
  });

  return results;
};
